import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, TrendingUp, ArrowLeft } from 'lucide-react'

export default function GanttChartsPage() {
  const navigate = useNavigate()
  const [tasks] = React.useState([
    { id: 1, name: 'Design Phase', start: '2026-05-01', end: '2026-05-15', progress: 100, team: 'Design' },
    { id: 2, name: 'Development', start: '2026-05-10', end: '2026-06-15', progress: 60, team: 'Dev' },
    { id: 3, name: 'Testing', start: '2026-06-01', end: '2026-06-25', progress: 30, team: 'QA' },
    { id: 4, name: 'Deployment', start: '2026-06-20', end: '2026-06-30', progress: 0, team: 'DevOps' },
  ])

  const dateRange = useMemo(() => {
    const allDates = tasks.flatMap(t => [new Date(t.start), new Date(t.end)])
    const minDate = new Date(Math.min(...allDates))
    const maxDate = new Date(Math.max(...allDates))
    return { min: minDate, max: maxDate, days: Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) }
  }, [tasks])

  const getTaskPosition = (startDate) => {
    const taskStart = new Date(startDate)
    const daysFromStart = Math.floor((taskStart - dateRange.min) / (1000 * 60 * 60 * 24))
    return (daysFromStart / dateRange.days) * 100
  }

  const getTaskWidth = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const taskDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24))
    return (taskDays / dateRange.days) * 100
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/projects')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Project Management
        </button>
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Gantt Charts</h1>
          <p className="text-slate-400">Project timeline and task dependencies</p>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 overflow-x-auto">
          <div className="min-w-full">
            <div className="flex mb-6">
              <div className="w-40 flex-shrink-0 pr-4 border-r border-blue-500/20">
                <h3 className="text-sm font-semibold text-slate-300">Task Name</h3>
              </div>
              <div className="flex-1 ml-4">
                <div className="flex h-8 gap-1">
                  {Array.from({ length: Math.min(dateRange.days, 30) }).map((_, i) => (
                    <div key={i} className="flex-1 text-xs text-slate-400 border-r border-slate-700/30">
                      {i % 5 === 0 ? String(i).padStart(2, '0') : ''}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {tasks.map((task) => (
              <div key={task.id} className="flex mb-4 items-center">
                <div className="w-40 flex-shrink-0 pr-4 border-r border-blue-500/20">
                  <p className="text-sm font-medium text-white">{task.name}</p>
                  <p className="text-xs text-slate-400">{task.team}</p>
                </div>
                <div className="flex-1 ml-4 relative h-12 bg-slate-700/30 rounded">
                  <div
                    className="absolute h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded"
                    style={{
                      left: `${getTaskPosition(task.start)}%`,
                      width: `${getTaskWidth(task.start, task.end)}%`,
                    }}
                  >
                    <div className="flex items-center justify-center h-full text-xs font-semibold text-white">
                      {task.progress}%
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-lg font-bold text-white mb-4">Timeline Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Project Start</span>
                <span className="text-white font-semibold">{dateRange.min.toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Project End</span>
                <span className="text-white font-semibold">{dateRange.max.toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Duration</span>
                <span className="text-white font-semibold">{dateRange.days} days</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Active Tasks</span>
                <span className="text-white font-semibold">{tasks.length}</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-lg font-bold text-white mb-4">Overall Progress</h3>
            <div className="space-y-4">
              {tasks.map((task) => (
                <div key={task.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-400">{task.name}</span>
                    <span className="text-white font-semibold">{task.progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500" style={{ width: `${task.progress}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
